import React, { useEffect, useState, useRef, useCallback } from "react";
import Application, { openDialog, mountComponent, unmountComponent } from "./Application";
import Authentication from './Authentication';

document.addEventListener("DOMContentLoaded", () => {
    const main = document.querySelector("main");
    if (main) {
        main.style.cursor = "pointer";
        mountComponent('root', Application);
        main.addEventListener("click", handleMainClick);
    }
});

const handleMainClick = () => {
    const main = document.querySelector("main");
    if (main && !main.querySelector("#editor-wrapper")) {
        main.removeEventListener("click", handleMainClick);
        mountComponent("html", InlineEditor, {
            id: main.id,
            originalContent: main.innerHTML,
            originalStyles: {
                cursor: main.style.cursor,
                overflow: main.style.overflow,
                height: main.style.height,
                minHeight: main.style.minHeight,
                maxHeight: main.style.maxHeight
            }
        });
    }
};

const InlineEditor = ({ id, originalContent, originalStyles }) => {
    const [isEditing, setIsEditing] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const contentRef = useRef(null);
    const toolbarRef = useRef(null);
    const wrapperRef = useRef(null);
    const savedCursorPosition = useRef(null);

    // Use props for original content and styles
    const originalHtmlRef = useRef(originalContent || "");
    const originalMainStyleRef = useRef(originalStyles || {});

    // Function to get cursor position as character offset
    const getCursorPosition = useCallback(() => {
        const content = contentRef.current;
        if (!content) return 0;

        const selection = window.getSelection();
        if (selection.rangeCount === 0) return 0;

        const range = selection.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(content);
        preCaretRange.setEnd(range.endContainer, range.endOffset);

        return preCaretRange.toString().length;
    }, []);

    // Function to set cursor position using character offset
    const setCursorPosition = useCallback((position) => {
        const content = contentRef.current;
        if (!content) return;

        const selection = window.getSelection();
        const range = document.createRange();

        let currentPosition = 0;
        const walker = document.createTreeWalker(
            content,
            NodeFilter.SHOW_TEXT,
            null,
            false
        );

        let node;
        while (node = walker.nextNode()) {
            const nextPosition = currentPosition + node.textContent.length;

            if (nextPosition >= position) {
                const offset = position - currentPosition;
                range.setStart(node, Math.min(offset, node.textContent.length));
                range.setEnd(node, Math.min(offset, node.textContent.length));
                break;
            }
            currentPosition = nextPosition;
        }

        // If we couldn't find the exact position, set it at the end
        if (currentPosition < position) {
            range.selectNodeContents(content);
            range.collapse(false);
        }

        selection.removeAllRanges();
        selection.addRange(range);
        content.focus();
    }, []);

    // Function to save current cursor position
    const saveCursorPosition = useCallback(() => {
        const position = getCursorPosition();
        savedCursorPosition.current = position;
    }, [getCursorPosition]);

    // Function to restore saved cursor position
    const restoreCursorPosition = useCallback(() => {
        if (savedCursorPosition.current !== null) {
            setCursorPosition(savedCursorPosition.current);
        }
    }, [setCursorPosition]);

    // Effect to set up the editor UI when the component mounts
    useEffect(() => {
        const main = document.querySelector("main");
        if (!main || main.querySelector("#editor-wrapper")) return;

        // Store original HTML and styles if not provided via props (fallback)
        if (!originalContent) {
            originalHtmlRef.current = main.innerHTML;
        }
        if (!originalStyles) {
            originalMainStyleRef.current = {
                cursor: main.style.cursor,
                overflow: main.style.overflow,
                height: main.style.height,
                minHeight: main.style.minHeight,
                maxHeight: main.style.maxHeight
            };
        }

        const wrapper = document.createElement("div");
        wrapper.id = "editor-wrapper";
        // Full screen styles
        wrapper.style.position = "fixed";
        wrapper.style.top = "0";
        wrapper.style.left = "0";
        wrapper.style.width = "100vw";
        wrapper.style.height = "100vh";
        wrapper.style.overflowY = "scroll";
        wrapper.style.background = "var(--theme)";
        wrapper.style.zIndex = "9999";
        wrapper.style.display = "flex";
        wrapper.style.flexDirection = "column";

        const toolbar = document.createElement("div");
        toolbar.id = "editor-toolbar";
        toolbar.style.display = "flex";
        toolbar.style.flexWrap = "wrap";
        toolbar.style.padding = "10px";
        toolbar.style.gap = "10px";

        const content = document.createElement("div");
        content.id = "editor-content";
        content.contentEditable = "true";
        content.innerHTML = originalHtmlRef.current;
        content.style.flex = "1";
        wrapper.style.top = "25px";
        content.style.overflow = "auto";
        content.style.outline = "none";
        content.style.padding = "20px";
        content.style.margin = "20px";

        wrapper.appendChild(toolbar);
        wrapper.appendChild(content);

        // Add to body instead of main for true full screen
        document.body.appendChild(wrapper);

        // Hide main content
        main.style.display = "none";

        contentRef.current = content;
        toolbarRef.current = toolbar;
        wrapperRef.current = wrapper;

        setIsEditing(true);
    }, []);

    const exitEditMode = useCallback(() => {
        const main = document.querySelector("main");
        const wrapper = wrapperRef.current;

        if (!main || !wrapper) return;

        setIsEditing(false);
        setHasChanges(false);

        // Remove the full screen wrapper
        wrapper.remove();

        // Restore main content
        main.style.display = "";
        main.innerHTML = originalHtmlRef.current;

        const originalStyles = originalMainStyleRef.current;
        main.style.cursor = originalStyles.cursor || "pointer";
        main.style.overflow = originalStyles.overflow || "";
        main.style.height = originalStyles.height || "";
        main.style.minHeight = originalStyles.minHeight || "";
        main.style.maxHeight = originalStyles.maxHeight || "";

        main.removeEventListener("click", handleMainClick);
        main.addEventListener("click", handleMainClick);

        unmountComponent('html');
    }, []);

    // Callback for saving content and exiting
    const saveContent = useCallback(async () => {
        const html = contentRef.current?.innerHTML;
        if (!html) return;
        setIsSaving(true);
        try {
            const res = await Authentication.fetchWithAuthentication("/api/communication/html", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id, html })
            });

            if (!res.ok) throw new Error("Error al guardar");

            // Update the original content with saved content
            originalHtmlRef.current = html;

            setHasChanges(false);

            // Exit edit mode after successful save
            exitEditMode();
        } catch (err) {
            console.error("Failed to save content:", err);
            alert("Fallo al guardar");
        } finally {
            setIsSaving(false);
        }
    }, [id, exitEditMode]);

    // Effect to render toolbar buttons
    useEffect(() => {
        const toolbar = toolbarRef.current;
        if (!toolbar) return;

        toolbar.innerHTML = "";
        toolbar.style.display = "flex";

        const createBtn = (label, onClick, disabled = false, variant = "normal") => {
            const btn = document.createElement("button");
            btn.textContent = label;
            btn.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick();
            };
            btn.disabled = disabled;
            btn.style.padding = "8px 16px";
            btn.style.fontSize = "14px";
            btn.style.borderRadius = "4px";
            btn.style.border = "1px solid rgba(255, 255, 255, 0.3)";
            btn.style.cursor = disabled ? "not-allowed" : "pointer";

            if (variant === "save") {
                btn.style.backgroundColor = hasChanges && !disabled ? "#4CAF50" : "rgba(76, 175, 80, 0.3)";
                btn.style.color = "white";
            } else if (variant === "exit") {
                btn.style.backgroundColor = "#f44336";
                btn.style.color = "white";
            } else {
                btn.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                btn.style.color = "white";
            }

            btn.addEventListener("mouseenter", () => {
                if (!disabled) {
                    btn.style.opacity = "0.8";
                }
            });

            btn.addEventListener("mouseleave", () => {
                btn.style.opacity = "1";
            });

            return btn;
        };

        const insertImage = () => {
            // Save current cursor position before opening dialog
            saveCursorPosition();

            openDialog("viewer", {
                entity: "content",
                id: `img-${Date.now()}`,
                alt: "New image"
            }, "Insert Image", "large");
        };

        const insertLink = () => {
            const url = window.prompt("Enter the URL:");
            if (url) {
                document.execCommand("createLink", false, url);
                setHasChanges(true);
            }
        };

        const exec = (cmd, val = null) => {
            document.execCommand(cmd, false, val);
            setHasChanges(true);
        };

        toolbar.appendChild(createBtn("H1", () => exec("formatBlock", "h1")));
        toolbar.appendChild(createBtn("H2", () => exec("formatBlock", "h2")));
        toolbar.appendChild(createBtn("H3", () => exec("formatBlock", "h3")));
        toolbar.appendChild(createBtn("Bold", () => exec("bold")));
        toolbar.appendChild(createBtn("Italic", () => exec("italic")));
        toolbar.appendChild(createBtn("Underline", () => exec("underline")));
        toolbar.appendChild(createBtn("Paragraph", () => exec("formatBlock", "p")));
        toolbar.appendChild(createBtn("🖼 Image", insertImage));
        toolbar.appendChild(createBtn("🔗 Link", insertLink));
        toolbar.appendChild(createBtn("💾 Save", saveContent, isSaving || !hasChanges, "save"));
        toolbar.appendChild(createBtn("✕ Exit", exitEditMode, false, "exit"));
    }, [isSaving, hasChanges, saveContent, exitEditMode, saveCursorPosition]);

    // Remove the click outside handler - only exit via buttons now
    // The original click outside handler is completely removed

    useEffect(() => {
        const content = contentRef.current;
        if (!content) return;

        const markChanged = () => setHasChanges(true);

        // Save cursor position on these events
        const handleCursorChange = () => {
            // Small delay to ensure the cursor position is stable
            setTimeout(() => {
                saveCursorPosition();
            }, 10);
        };

        content.addEventListener("input", markChanged);
        content.addEventListener("keyup", handleCursorChange);
        content.addEventListener("mouseup", handleCursorChange);
        content.addEventListener("focus", handleCursorChange);

        return () => {
            content.removeEventListener("input", markChanged);
            content.removeEventListener("keyup", handleCursorChange);
            content.removeEventListener("mouseup", handleCursorChange);
            content.removeEventListener("focus", handleCursorChange);
        };
    }, [saveCursorPosition]);

    // Effect for handling image selection via custom event
    useEffect(() => {
        const handleImageSelected = (event) => {
            const { imageUrl, keywordsText } = event.detail;
            const content = contentRef.current;
            if (!content) return;
            // Create the image element with delete functionality
            const img = document.createElement("img");
            img.src = imageUrl;
            img.title = keywordsText || "Image";
            img.style.maxWidth = "100%";
            img.style.display = "block";
            img.style.margin = "10px 0";
            img.style.cursor = "pointer";
            img.style.border = "2px solid transparent";
            img.style.transition = "border-color 0.2s";

            // Add hover effect
            img.addEventListener("mouseenter", () => {
                img.style.borderColor = "#ff4444";
            });

            img.addEventListener("mouseleave", () => {
                img.style.borderColor = "transparent";
            });

            // Add click to delete functionality
            img.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (confirm("Delete this image?")) {
                    img.remove();
                    setHasChanges(true);
                }
            });

            // Method 1: Try to restore cursor position and insert at that position
            if (savedCursorPosition.current !== null) {
                try {
                    // Get the current position based on saved cursor position
                    const position = savedCursorPosition.current;

                    // Create a temporary div to hold the image HTML
                    const tempDiv = document.createElement('div');
                    tempDiv.appendChild(img);
                    const imageHtml = tempDiv.innerHTML;

                    // Get current content
                    const currentContent = content.innerHTML;

                    // Convert HTML to text to find the correct insertion point
                    const tempContent = document.createElement('div');
                    tempContent.innerHTML = currentContent;
                    const textContent = tempContent.textContent || tempContent.innerText || '';

                    // Find insertion point in HTML
                    let htmlPosition = 0;
                    let textPosition = 0;

                    // Simple approach: if we have a saved position, try to insert there
                    if (position <= textContent.length) {
                        // Create a range and set it to the saved position
                        const selection = window.getSelection();
                        const range = document.createRange();

                        let currentPos = 0;
                        const walker = document.createTreeWalker(
                            content,
                            NodeFilter.SHOW_TEXT,
                            null,
                            false
                        );

                        let node;
                        let targetNode = null;
                        let targetOffset = 0;

                        while (node = walker.nextNode()) {
                            const nextPos = currentPos + node.textContent.length;

                            if (nextPos >= position) {
                                targetNode = node;
                                targetOffset = position - currentPos;
                                break;
                            }
                            currentPos = nextPos;
                        }

                        if (targetNode) {
                            range.setStart(targetNode, targetOffset);
                            range.setEnd(targetNode, targetOffset);

                            // Insert the image at the range
                            range.insertNode(img);

                            // Position cursor after the image
                            range.setStartAfter(img);
                            range.collapse(true);

                            selection.removeAllRanges();
                            selection.addRange(range);

                            setHasChanges(true);
                            return;
                        }
                    }
                } catch (error) {
                    console.error("Failed to insert at saved position:", error);
                }
            }

            // Method 2: Fallback - insert at current cursor position or end
            try {
                const selection = window.getSelection();
                let range;

                if (selection.rangeCount > 0) {
                    range = selection.getRangeAt(0);
                } else {
                    range = document.createRange();
                    range.selectNodeContents(content);
                    range.collapse(false);
                }

                range.insertNode(img);

                // Position cursor after the image
                range.setStartAfter(img);
                range.collapse(true);

                selection.removeAllRanges();
                selection.addRange(range);

                setHasChanges(true);

            } catch (error) {
                console.error("Failed to insert image:", error);
                // Final fallback: just append to content
                content.appendChild(img);
                setHasChanges(true);
            }
        };

        document.addEventListener("imageSelected", handleImageSelected);
        return () => document.removeEventListener("imageSelected", handleImageSelected);
    }, []);

    // Function to edit existing links
    const editLink = useCallback((event) => {
        const target = event.target;
        if (target.tagName === "A") {
            event.preventDefault();
            event.stopPropagation();

            const currentUrl = target.href;
            const newUrl = window.prompt("Edit link URL:", currentUrl);

            if (newUrl === null) {
                return;
            }

            if (newUrl.trim() === "") {
                const parent = target.parentNode;
                if (parent) {
                    const fragment = document.createDocumentFragment();
                    while (target.firstChild) {
                        fragment.appendChild(target.firstChild);
                    }
                    parent.replaceChild(fragment, target);
                    setHasChanges(true);
                }
            } else {
                target.href = newUrl;
                setHasChanges(true);
            }
        }
    }, []);

    // Effect to add click listener for link editing and existing image deletion
    useEffect(() => {
        const content = contentRef.current;
        if (!content) return;

        const handleContentClick = (event) => {
            const target = event.target;

            // Handle link editing
            if (target.tagName === "A") {
                editLink(event);
                return;
            }

            // Handle existing image deletion
            if (target.tagName === "IMG") {
                event.preventDefault();
                event.stopPropagation();

                // Add delete functionality to existing images that don't have it
                if (!target.hasAttribute('data-deletable')) {
                    target.setAttribute('data-deletable', 'true');
                    target.style.cursor = "pointer";
                    target.style.border = "2px solid transparent";
                    target.style.transition = "border-color 0.2s";

                    // Add hover effect
                    target.addEventListener("mouseenter", () => {
                        target.style.borderColor = "#ff4444";
                    });

                    target.addEventListener("mouseleave", () => {
                        target.style.borderColor = "transparent";
                    });
                }

                if (confirm("Delete this image?")) {
                    target.remove();
                    setHasChanges(true);
                }
            }
        };

        content.addEventListener("click", handleContentClick);

        return () => {
            content.removeEventListener("click", handleContentClick);
        };
    }, [editLink]);

    // Effect for keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isEditing) return;

            if (e.key === "Escape") {
                e.preventDefault();
                exitEditMode();
            }

            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                if (hasChanges && !isSaving) {
                    saveContent();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isEditing, saveContent, exitEditMode, hasChanges, isSaving]);

    return null;
};

export default InlineEditor;